
import React, { createContext, useContext, useMemo } from 'react';
import { AiAssistantContextProps } from '@/types/ai-assistant-context';

/**
 * Create the AI Assistant context
 */
export const AiAssistantContext = createContext<AiAssistantContextProps | undefined>(undefined);

/**
 * Hook to access AI Assistant context with proper error handling
 * @throws Error if used outside of AiAssistantProvider
 */
export const useAiAssistant = () => {
  const context = useContext(AiAssistantContext);
  
  if (context === undefined) {
    console.error('[USE_AI_ASSISTANT] Context is undefined - provider not found');
    throw new Error('useAiAssistant must be used within an AiAssistantProvider');
  }
  return context;
};

/**
 * Create optimized context value directly from dependencies
 */
export const createContextValue = (dependencies: any): AiAssistantContextProps => {
  // Validate required dependencies
  const requiredKeys = [
    'createNewSession', 'deleteSession', 'updateSessionTitle', 'selectSession',
    'searchSessions', 'loadSession', 'setCurrentSession', 'createNewConversation',
    'clearMessages', 'loadMoreMessages', 'updateMessageFeedback',
    'messages', 'currentSession', 'sessions', 'isLoading', 'isTyping',
    'fetchingSessions', 'hasMoreMessages', 'isLoadingMoreMessages',
    'sendMessage'
  ];
  
  const missingKeys = requiredKeys.filter(key => !(key in dependencies));
  if (missingKeys.length > 0) {
    console.error('[CREATE_CONTEXT_VALUE] Missing required dependencies:', missingKeys);
    throw new Error(`Missing required dependencies: ${missingKeys.join(', ')}`);
  }

  const contextValue = {
    // Session management
    createNewSession: dependencies.createNewSession,
    deleteSession: dependencies.deleteSession,
    updateSessionTitle: dependencies.updateSessionTitle,
    selectSession: dependencies.selectSession,
    searchSessions: dependencies.searchSessions,
    loadSession: dependencies.loadSession,
    setCurrentSession: dependencies.setCurrentSession,
    createNewConversation: dependencies.createNewConversation,
    
    // Message interactions
    clearMessages: dependencies.clearMessages,
    loadMoreMessages: dependencies.loadMoreMessages,
    updateMessageFeedback: dependencies.updateMessageFeedback,
    
    // Dynamic state
    messages: dependencies.messages,
    currentSession: dependencies.currentSession,
    sessions: dependencies.sessions,
    isLoading: dependencies.isLoading,
    isTyping: dependencies.isTyping,
    fetchingSessions: dependencies.fetchingSessions,
    hasMoreMessages: dependencies.hasMoreMessages,
    isLoadingMoreMessages: dependencies.isLoadingMoreMessages,
    
    // Real usage state from dependencies
    usageStats: dependencies.usageStats || [],
    usageLoading: dependencies.usageLoading || false,
    getUsageForTopic: dependencies.getUsageForTopic || (() => null),
    
    // Actions
    sendMessage: dependencies.sendMessage,
    usageUpdateTrigger: dependencies.usageUpdateTrigger || 0
  };
  
  return contextValue;
};
